import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Form, Loader } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import { useUpdateProspect } from '../../../../../api/services/lupp/prospect/prospect';
import type {
  Prospect,
  UpdateProspectRequest,
} from '../../../../../api/services/lupp/prospect/dto.prospect';
import { useGetLabels } from '../../../../../hooks/useLabels';
import { usePostalCodeLookup } from '../../../../../hooks/usePostalCodeLookup';
import {
  formatOffsetDateTime,
  formatDateForPicker,
  buildProspectPayload,
  updateProspectContactTrackings,
} from '../utils';
import { convertToContentfulLocale } from '../../../../../utils/tools';
import { createLeadFormFields, createTestDriveFormFields } from './formFields';
import styles from './DetailsTab.module.scss';

/** Delay before redirecting after successful form submission (ms) */
const REDIRECT_DELAY = 3000;
/** Delay to allow form initialization before setting values (ms) */
const FORM_INIT_DELAY = 300;
/** Small delay to ensure dirty state update propagates before closing (ms) */
const FORM_SUCCESS_DELAY = 50;

type ProspectDetailProps = {
  prospect?: Prospect;
  onDirtyChange?: (isDirty: boolean) => void;
  onCancel?: () => void;
  isTestDriveTab?: boolean;
};

const DetailsTab: React.FC<ProspectDetailProps> = ({
  prospect,
  onDirtyChange,
  onCancel,
  isTestDriveTab = false,
}) => {
  const { userIdentity } = useStore();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const { mutate, isPending } = useUpdateProspect();
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const isInitialMount = useRef(true);
  const messageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormRef>(null);

  // Use refs to keep stable references and prevent form reset
  const onCancelRef = useRef(onCancel);
  const onDirtyChangeRef = useRef(onDirtyChange);

  const location = userIdentity?.addressLine1 || '-';
  const organizationName = userIdentity?.organizationName;
  const prospectOwnerName =
    `${userIdentity?.firstName || ''} ${userIdentity?.lastName || ''}`.trim();
  const prospectOwnerLocation =
    `${userIdentity?.city || ''} , ${userIdentity?.countryCode || ''}`.trim();
  // Postal code lookup for auto-populating city
  const { locationData } = usePostalCodeLookup(postalCode, {
    debounceMs: 500,
    enabled: postalCode.length >= 3,
  });

  // Store location data from HereMaps for use in payload
  const [hereLocationData, setHereLocationData] = useState<{
    city?: string;
    stateCode?: string;
    countryCode?: string;
  }>({});

  // Auto-populate city and country, store location data when available
  useEffect(() => {
    if (locationData && formRef.current) {
      // Use the form ref's setValue method to properly update the field with validation
      if (locationData.city) {
        formRef.current.setValue('city', locationData.city);
      }
      if (locationData.country) {
        formRef.current.setValue('country', locationData.country);
      }
      // Store location data for use in payload
      setHereLocationData({
        city: locationData.city || undefined,
        stateCode: locationData.stateCode || undefined,
        countryCode: locationData.countryCode || undefined,
      });
    }
  }, [locationData]);

  // Update Next Engagement Date field when prospect data changes (from status modal updates)
  useEffect(() => {
    if (formRef.current && prospect && !isTestDriveTab) {
      const statusName = prospect.statusName;
      if (statusName === 'Engaged' || statusName === 'Contacting') {
        const activeTracking = prospect.prospectContactTrackings?.find(
          (tracking) => tracking.isActive === true
        );
        if (activeTracking?.nextToBeContacted) {
          const nextEngagementDate = formatDateForPicker(activeTracking.nextToBeContacted);
          formRef.current.setValue('nextEngagementDate', nextEngagementDate);
        }
      }
    }
  }, [prospect?.prospectContactTrackings, isTestDriveTab, prospect]);

  const handlePostalCodeChange = useCallback((value: unknown) => {
    // Extract value from event if it's an event object, otherwise use the value directly
    if (value && typeof value === 'object' && 'target' in value) {
      const event = value as React.ChangeEvent<HTMLInputElement>;
      setPostalCode(event.target.value || '');
    } else {
      setPostalCode(String(value || ''));
    }
  }, []);

  // Update refs when props change
  useEffect(() => {
    onCancelRef.current = onCancel;
    onDirtyChangeRef.current = onDirtyChange;
  }, [onCancel, onDirtyChange]);

  const { data: labels } = useGetLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  // Reset initial mount flag when prospect or tab changes
  useEffect(() => {
    isInitialMount.current = true;
    // Notify parent that form is clean on mount/tab change
    onDirtyChangeRef.current?.(false);
    // Delay to allow form to fully initialize before accepting dirty state changes
    // The Form component's watch() triggers immediately, so we need to wait longer
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, FORM_INIT_DELAY);
    return () => clearTimeout(timer);
  }, [prospect?.prospectIdentifier, isTestDriveTab]);

  const handleFormDirtyChange = useCallback((isDirty: boolean) => {
    // Ignore dirty state changes during initial mount to prevent false positives
    if (isInitialMount.current) {
      return;
    }
    onDirtyChangeRef.current?.(isDirty);
  }, []);

  // Stable cancel handler that doesn't change reference
  const handleCancel = useCallback(() => {
    onCancelRef.current?.();
  }, []);

  // Get the first active test drive schedule for the form
  const activeTestDriveSchedule = useMemo(() => {
    const schedules = prospect?.testDriveSchedules || [];
    return schedules.find((schedule) => schedule.isActive && !schedule.isDeleted) || schedules[0];
  }, [prospect?.testDriveSchedules]);

  const formFields = useMemo(
    () =>
      isTestDriveTab
        ? createTestDriveFormFields(
            prospect,
            handleCancel,
            activeTestDriveSchedule,
            location,
            organizationName
          )
        : createLeadFormFields(
            prospect,
            handleCancel,
            organizationName,
            prospectOwnerName,
            prospectOwnerLocation,
            handlePostalCodeChange
          ),
    [
      prospect,
      handleCancel,
      isTestDriveTab,
      activeTestDriveSchedule,
      location,
      organizationName,
      prospectOwnerName,
      prospectOwnerLocation,
      handlePostalCodeChange,
    ]
  );

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        // Ensure dirty state is cleared before closing
        onDirtyChangeRef.current?.(false);
        // Small delay to ensure state update propagates
        setTimeout(() => {
          onCancelRef.current?.();
        }, FORM_SUCCESS_DELAY);
      }, REDIRECT_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [successMessage]);

  // Scroll to message when error or success message appears
  useEffect(() => {
    if ((errorMessage || successMessage) && messageRef.current) {
      // Find the scrollable parent (slider body with overflow-y: auto)
      let parent = messageRef.current.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        }
        parent = parent.parentElement;
      }
    }
  }, [errorMessage, successMessage]);

  const handleSuccess = (data: unknown) => {
    if (!prospect?.prospectIdentifier) {
      setErrorMessage('Prospect ID is missing. Cannot update.');
      return;
    }

    // Prevent updates when status is Closed
    if (prospect?.statusName?.toLowerCase() === 'closed') {
      setErrorMessage(
        labels?.closedProspectError ||
          'Cannot update a closed prospect. Please reopen the prospect to make changes.'
      );
      setSuccessMessage('');
      return;
    }

    setErrorMessage(null);

    // Get current timestamp for all date fields
    const currentDate = new Date();
    const now = formatOffsetDateTime(currentDate);

    let payload: UpdateProspectRequest;

    if (isTestDriveTab) {
      // Handle Test Drive form data
      const testDriveFormData = data as {
        testDriveNow?: string[];
        scheduledDate?: string;
        scheduledTime?: string;
        status?: string;
        selectedModelTrim?: string;
      };

      // Parse time slot to get start and end times
      const timeSlot = testDriveFormData.scheduledTime || '';
      const [startTime, endTime] = timeSlot.split('-').map((t) => t?.trim() || '');

      // Build scheduled datetime from date and time
      const scheduledDate = testDriveFormData.scheduledDate || '';
      const buildDatetime = (date: string, time: string): string => {
        if (!date || !time) return now;
        try {
          // Convert date to YYYY-MM-DD format if needed
          let isoDate = date;
          if (date.includes('/')) {
            // Date is in MM/DD/YYYY format, convert to YYYY-MM-DD
            const parts = date.split('/');
            if (parts.length === 3) {
              const [month, day, year] = parts;
              isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
          }

          const [hours, minutes] = time.split(':').map(Number);
          const hour24 = hours < 6 ? hours + 12 : hours;
          return `${isoDate}T${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00Z`;
        } catch {
          return now;
        }
      };

      // Update or create test drive schedule
      const existingSchedule = activeTestDriveSchedule;
      const updatedTestDriveSchedule = {
        testDriveScheduleId: existingSchedule?.testDriveScheduleId || undefined,
        sfdcSalesEventId: existingSchedule?.sfdcSalesEventId,
        businessPartnerUserId: existingSchedule?.businessPartnerUserId,
        businessPartnerOrganizationId: existingSchedule?.businessPartnerOrganizationId,
        testDriveStatus: testDriveFormData.status || existingSchedule?.testDriveStatus,
        scheduledStartDatetime: buildDatetime(scheduledDate, startTime),
        scheduledEndDatetime: buildDatetime(scheduledDate, endTime),
        appointmentLocationName:
          existingSchedule?.appointmentLocationName ||
          prospect.prospectOwnerLocation ||
          location ||
          '',
        model: existingSchedule?.model,
        trim: testDriveFormData.selectedModelTrim || existingSchedule?.trim,
        lastSyncedToSalesforce: existingSchedule?.lastSyncedToSalesforce,
        timezone: existingSchedule?.timezone || 'GMT',
        isActive:
          testDriveFormData.testDriveNow?.includes('yes') ?? existingSchedule?.isActive ?? true,
        isDeleted: false,
        createdDatetime: existingSchedule?.createdDatetime || now,
        updatedDatetime: now,
        createdBy: existingSchedule?.createdBy,
        updatedBy: 'portal-user',
      };

      payload = buildProspectPayload(prospect, now, {
        testDriveSchedules: [updatedTestDriveSchedule],
      });
    } else {
      // Handle Details form data
      // Note: Form's sanitizeData() uses apiKey for output, so keys match apiKey values from formFields
      const formData = data as {
        leadFirstName: string;
        prospectMiddleName?: string;
        leadLastName: string;
        prospectTitle?: string;
        leadEmail: string;
        leadPhone?: string;
        zipCode?: string;
        city?: string;
        interestedModel?: string[];
        interestedVehicleCondition?: string;
        currentCarMake?: string;
        purchaseType?: string;
        financeType?: string;
        purchaseTiming?: string;
        nextEngagementDate?: string;
        preferredContactMethod?: string;
        noteText?: string;
        isEmailOptIn?: string;
        isCallOptIn?: string;
      };

      payload = buildProspectPayload(prospect, now, {
        // Address fields from form
        city: formData.city || prospect.city,
        zipCode: formData.zipCode || prospect.zipCode,
        stateProvince: hereLocationData.stateCode || prospect.stateProvince,
        countryCode: hereLocationData.countryCode || prospect.countryCode,

        // Finance and vehicle fields from form
        financeType: formData.financeType || prospect.financeType,
        currentCarMake: formData.currentCarMake || prospect.currentCarMake,

        // Note from form
        noteText: formData.noteText || prospect.noteText,

        // Prospect personal info from form (using apiKey names) - EXCLUDE EMAIL
        prospectFirstName: formData.leadFirstName || prospect.prospectFirstName || '',
        prospectMiddleName: formData.prospectMiddleName || prospect.prospectMiddleName,
        prospectLastName: formData.leadLastName || prospect.prospectLastName || '',
        prospectTitle: formData.prospectTitle || prospect.prospectTitle,
        prospectPhone: formData.leadPhone || prospect.prospectPhone,

        // Consent fields from form
        isEmailOptIn: formData.isEmailOptIn === 'emailOptIn',
        isCallOptIn: formData.isCallOptIn === 'callOptIn',

        // Interest fields from form
        interestedModel: Array.isArray(formData.interestedModel)
          ? formData.interestedModel.join(',')
          : formData.interestedModel || prospect.interestedModel,
        interestedVehicleCondition:
          formData.interestedVehicleCondition || prospect.interestedVehicleCondition,
        purchaseType: formData.purchaseType || prospect.purchaseType,
        purchaseTiming: formData.purchaseTiming || prospect.purchaseTiming,
        preferredContactMethod: formData.preferredContactMethod || prospect.preferredContactMethod,

        // Update nextToBeContacted for the tracking where isActive: true
        prospectContactTrackings: updateProspectContactTrackings(
          prospect,
          formData.nextEngagementDate,
          now
        ),
      });
    }

    // Remove email from payload to prevent updates (applies to both test drive and details)
    const { prospectEmail: _prospectEmail, ...payloadWithoutEmail } = payload;
    payload = payloadWithoutEmail as Prospect;

    mutate(
      { id: prospect.prospectIdentifier, data: payload },
      {
        onSuccess: () => {
          // Reset dirty state to prevent unsaved changes modal
          onDirtyChangeRef.current?.(false);
          setSuccessMessage(
            labels?.prospectUpdatedSuccess || 'Prospect updated successfully! Redirecting...'
          );
        },
        onError: (error: unknown) => {
          setSuccessMessage('');
          const errorMsg =
            error instanceof Error
              ? error.message
              : labels?.prospectUpdateError || 'Failed to update prospect. Please try again.';
          setErrorMessage(errorMsg);
        },
      }
    );
  };

  const handleError = (error: unknown) => {
    console.error('Form validation failed:', error);
    setErrorMessage(labels?.formValidationError || 'Please fix the form errors before submitting.');
    setSuccessMessage('');
  };

  return (
    <Box ref={messageRef}>
      {successMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {successMessage}
          </Typography>
        </Box>
      )}
      {errorMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {errorMessage}
          </Typography>
        </Box>
      )}
      {isPending && (
        <Box className="fetchingOverlay">
          <Loader size={32} />
        </Box>
      )}
      <Form
        ref={formRef}
        id="create-prospect-form"
        theme={invertedTheme}
        formFields={formFields}
        onSuccess={handleSuccess}
        onError={handleError}
        onDirtyChange={handleFormDirtyChange}
        isFluid={true}
      />
    </Box>
  );
};

export default DetailsTab;
